import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import Pagination from '../components/ui/pagination';
import { SearchInput } from '../components/ui/SearchInput';
import { Tabs } from '../components/ui/tabs';
import { FilterSelect } from '../components/ui/FilterSelect';
import { Select } from '../components/ui/Select';
import { Toggle } from '../components/ui/toggle';
import { Modal } from '../components/ui/modal';
import { Toast } from '../components/ui/toast';
import { Table, TableHead, TableHeaderCell, TableBody, TableRow, TableCell } from '../components/ui/table';

const TABS = [
  { value: 'safe', label: 'Safe' },
  { value: 'watch', label: 'Watch' },
  { value: 'risk', label: 'Risk' },
];

/**
 * Internal, admin-only reference page for the ui/ component kit — every
 * Button/Badge/Card/Pagination/Progress/SearchInput/Tabs/Toggle/Modal/Toast
 * variant from the epic design_html "Components" sheet in one place, so the
 * kit can be checked visually in both themes without touching a feature page.
 */
const FILTER_OPTIONS = [
  { value: 'all', label: 'All statuses' },
  { value: 'safe', label: 'Safe' },
  { value: 'risk', label: 'Risk' },
];

const UiKitShowcase: React.FC = () => {
  const [tab, setTab] = useState('safe');
  const [notifyOn, setNotifyOn] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [pillMode, setPillMode] = useState<'manual' | 'database'>('manual');
  const [filterValue, setFilterValue] = useState('all');
  const [selectValue, setSelectValue] = useState('safe');

  return (
    <div className="min-h-screen bg-canvas p-8 space-y-8">
      <h1 className="text-2xl font-semibold text-ink">UI Kit</h1>

      <Card>
        <CardHeader>
          <CardTitle>Buttons</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-3">
          <Button variant="default">Primary</Button>
          <Button variant="secondary">Solid</Button>
          <Button variant="outline">Outline</Button>
          <Button variant="ghost">Ghost</Button>
          <Button variant="destructive">Danger</Button>
        </CardContent>
        <CardContent className="flex flex-wrap items-center gap-3 pt-0">
          <div className="inline-flex rounded-full bg-ghost-bg p-[3px]" data-testid="pill-toggle-demo">
            <Button variant="pill" active={pillMode === 'manual'} onClick={() => setPillMode('manual')}>
              Manual Input
            </Button>
            <Button variant="pill" active={pillMode === 'database'} onClick={() => setPillMode('database')}>
              From Database
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Badges</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-3">
          <Badge variant="default">Default</Badge>
          <Badge variant="secondary">Secondary</Badge>
          <Badge variant="outline">Outline</Badge>
          <Badge variant="safe">Safe</Badge>
          <Badge variant="warn">Warn</Badge>
          <Badge variant="risk">Risk</Badge>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Search input</CardTitle>
        </CardHeader>
        <CardContent>
          <SearchInput
            placeholder="Search brands, domains, briefs…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Row controls (42px standard)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-center gap-3" data-testid="row-controls-demo">
            <SearchInput
              placeholder="Search…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-56"
            />
            <Button variant="default" size="md">Row button</Button>
            <Tabs tabs={TABS} value={tab} onChange={setTab} />
            <FilterSelect
              value={filterValue}
              onChange={setFilterValue}
              options={FILTER_OPTIONS}
              data-testid="row-controls-filter-select"
            />
            <div className="w-40">
              <Select
                value={selectValue}
                onChange={setSelectValue}
                options={FILTER_OPTIONS}
                data-testid="row-controls-select"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Segmented tabs</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Tabs tabs={TABS} value={tab} onChange={setTab} />
          <div className="flex items-center gap-3">
            <span className="text-sm text-ink">Notify on new matches</span>
            <Toggle checked={notifyOn} onChange={setNotifyOn} aria-label="Notify on new matches" />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Progress</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Progress value={72} className="h-2" />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Table</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>Domain</TableHeaderCell>
                <TableHeaderCell>Match rate</TableHeaderCell>
                <TableHeaderCell>Status</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>nike.com</TableCell>
                <TableCell>94%</TableCell>
                <TableCell><Badge variant="safe">Safe</Badge></TableCell>
              </TableRow>
              <TableRow>
                <TableCell>example-risky.com</TableCell>
                <TableCell>41%</TableCell>
                <TableCell><Badge variant="risk">Risk</Badge></TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Pagination</CardTitle>
        </CardHeader>
        <CardContent>
          <Pagination
            pagination={{
              page: 2,
              totalPages: 5,
              hasNextPage: true,
              hasPreviousPage: true,
              nextPage: 3,
              previousPage: 1,
              totalCount: 46,
              limit: 10,
            }}
            onPageChange={() => {}}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Toast</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <Toast variant="safe">Brief published successfully.</Toast>
          <Toast variant="risk">Fetch failed for 3 URLs.</Toast>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Modal</CardTitle>
        </CardHeader>
        <CardContent>
          <Button variant="secondary" onClick={() => setModalOpen(true)}>Open modal</Button>
          <Modal open={modalOpen} onClose={() => setModalOpen(false)} title="Delete brief">
            <p className="text-sm text-ink-muted">This can't be undone. Are you sure you want to delete this brief?</p>
            <div className="mt-6 flex justify-end gap-3">
              <Button variant="ghost" onClick={() => setModalOpen(false)}>Cancel</Button>
              <Button variant="destructive" onClick={() => setModalOpen(false)}>Delete</Button>
            </div>
          </Modal>
        </CardContent>
      </Card>
    </div>
  );
};

export default UiKitShowcase;
